import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, MapPin } from "lucide-react";

const FOUNDER_IMG = "/founder.jpg";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-16 md:pt-24 pb-16 md:pb-20 overflow-hidden"
    >
      {/* Subtle background grid */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,0.04)_1px,transparent_1px)] bg-[size:48px_48px]" />
      </div>

      {/* Soft accent blob */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-20 w-[520px] h-[520px] bg-emerald-100/40 rounded-full blur-3xl"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
        {/* Left: Text */}
        <div className="md:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full bg-white border border-zinc-200 px-3.5 py-1.5 text-[11px] font-semibold tracking-widest uppercase text-zinc-700 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Solo-Entwickler · Karlsruhe
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-5xl sm:text-6xl lg:text-[5.25rem] xl:text-[6rem] font-light tracking-tighter text-zinc-900 mt-6 leading-[0.98]"
          >
            Ich baue <br className="hidden sm:block" />
            <span className="italic font-normal">Deine</span> Website. <br />
            <span className="text-zinc-400">Persönlich.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-7 text-lg md:text-xl text-zinc-600 leading-relaxed max-w-xl"
          >
            Kein Account-Manager, keine Hotline. Ich bin Fabian — und ich
            entwerfe, entwickle und betreue Deine Website von A bis Z. Vom
            One-Pager bis zum Online-Shop.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/kontakt"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-zinc-900 text-white text-base font-medium px-7 py-4 hover:bg-zinc-800 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(0,0,0,0.35)]"
            >
              <Calendar size={17} />
              Kostenlosen Beratungstermin buchen
              <ArrowUpRight
                size={17}
                className="group-hover:rotate-45 transition-transform duration-300"
              />
            </Link>
            <Link
              to="/preise"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center rounded-full border border-zinc-300 text-zinc-900 text-base font-medium px-7 py-4 bg-white hover:bg-zinc-50 hover:border-zinc-400 transition-all"
            >
              Preise ansehen
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex items-center gap-2 text-sm text-zinc-500"
          >
            <MapPin size={14} className="text-zinc-400" />
            <span>
              Aus Karlsruhe · für ganz Deutschland · Antwort innerhalb 24 Std.
            </span>
          </motion.div>
        </div>

        {/* Right: Portrait */}
        <div className="md:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
            className="relative"
          >
            {/* Decorative accent behind photo */}
            <div
              aria-hidden
              className="absolute -inset-3 bg-gradient-to-br from-zinc-900/8 via-emerald-200/40 to-amber-200/30 rounded-[2.5rem] blur-2xl opacity-60"
            />

            <div className="relative aspect-[4/5] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden bg-zinc-100 border border-zinc-100 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.18)]">
              <picture>
                <source srcSet="/founder.webp" type="image/webp" />
                <img
                  src={FOUNDER_IMG}
                  alt="Fabian Winkler, Gründer von NovaSite"
                  width="608"
                  height="760"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                  style={{ objectPosition: "50% 20%" }}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
              {/* Soft gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />

              {/* Name card */}
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/85 backdrop-blur-xl rounded-2xl px-4 py-3.5 border border-white/60 shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display text-base font-medium text-zinc-900 leading-tight">
                        Fabian Winkler
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 uppercase tracking-widest">
                        Gründer · NovaSite
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                        22 J. · seit 2026
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
